// Driver Edit -> Payments: the driver's pay arrangement history ("Commission until
// 30 Sep, Lease from 1 Oct"). The weekly payout prices each job by the arrangement in force
// on the job's date, so a driver can change type mid-week and still be paid correctly.
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { deletePayArrangement, loadPayArrangements, savePayArrangement, type PayArrangement } from "@/lib/payArrangements";

interface TypeOption {
  id: string;
  type_name: string;
}

interface Props {
  driverName: string;
  driverTypes: TypeOption[];
  /** Called once the history is loaded or changed, with how many entries there are. */
  onCount?: (count: number) => void;
  /** The driver's current type after a change (the database keeps drivers.driver_type_id in step). */
  onCurrentTypeChanged?: (driverTypeId: string | null) => void;
}

const fmt = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });

const DriverPayArrangements: React.FC<Props> = ({ driverName, driverTypes, onCount, onCurrentTypeChanged }) => {
  const [rows, setRows] = useState<PayArrangement[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [from, setFrom] = useState("");
  const [typeId, setTypeId] = useState("");
  const [note, setNote] = useState("");

  const typeName = (id: string) => driverTypes.find((t) => t.id === id)?.type_name ?? "Unknown type";

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const map = await loadPayArrangements([driverName]);
      const list = map[driverName] ?? [];
      setRows(list);
      onCount?.(list.length);
    } finally {
      setLoading(false);
    }
  }, [driverName, onCount]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pushCurrentType = async () => {
    const { data } = await supabase.from("drivers").select("driver_type_id").eq("driver_name", driverName).maybeSingle();
    onCurrentTypeChanged?.((data as any)?.driver_type_id ?? null);
  };

  const add = async () => {
    if (!from || !typeId) {
      toast({ title: "Pick a date and a type", description: "Choose the first day the new arrangement applies, and the driver type.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await savePayArrangement({ driverName, effectiveFrom: from, driverTypeId: typeId, note });
      toast({ title: "Pay arrangement saved", description: `${typeName(typeId)} from ${fmt(from)}.` });
      setFrom("");
      setTypeId("");
      setNote("");
      await refresh();
      await pushCurrentType();
    } catch (e: any) {
      toast({ title: "Couldn't save", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: PayArrangement) => {
    if (!window.confirm(`Remove "${typeName(row.driver_type_id)} from ${fmt(row.effective_from)}"? Weeks that haven't been paid yet will be priced without it.`)) return;
    try {
      await deletePayArrangement(row.id);
      await refresh();
      await pushCurrentType();
    } catch (e: any) {
      toast({ title: "Couldn't remove", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  const today = todayIso();
  // The entry in force today is the last one that has started.
  const currentId = [...rows].reverse().find((r) => r.effective_from.slice(0, 10) <= today)?.id;

  return (
    <div className="md:col-span-2 rounded-lg border border-gray-700 p-3 space-y-3">
      <div>
        <p className="text-sm font-semibold">Pay arrangement history</p>
        <p className="text-xs text-gray-400">
          Changing from commission to lease (or back) part-way through a week? Add the date it starts here. The weekly
          payout prices each job by the arrangement in force on that day, and the payslip shows the split. Set the lease
          fee up under Deductions as <em>Recurring</em> with <em>Pro-rate part weeks</em> ticked.
        </p>
      </div>

      {loading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">No changes recorded — {driverName} is paid on their current driver type for every day.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400">
                <th className="py-1 pr-3">From</th>
                <th className="py-1 pr-3">Driver type</th>
                <th className="py-1 pr-3">Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-800">
                  <td className="py-1 pr-3 whitespace-nowrap">{r.effective_from.slice(0, 10) === "2000-01-01" ? "Always (until the next change)" : fmt(r.effective_from)}</td>
                  <td className="py-1 pr-3">
                    {typeName(r.driver_type_id)}
                    {r.id === currentId && <span className="ml-2 rounded bg-green-900/50 px-1.5 py-0.5 text-[10px] text-green-300">current</span>}
                    {r.effective_from.slice(0, 10) > today && <span className="ml-2 rounded bg-amber-900/50 px-1.5 py-0.5 text-[10px] text-amber-300">upcoming</span>}
                  </td>
                  <td className="py-1 pr-3 text-xs text-gray-400">{r.note ?? ""}</td>
                  <td className="py-1 text-right">
                    <Button type="button" variant="ghost" size="sm" onClick={() => remove(r)}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <Label>Starts on</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label>Driver type from then</Label>
          <Select value={typeId} onValueChange={setTypeId}>
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {driverTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.type_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Note (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Took on the lease" />
        </div>
        <Button type="button" onClick={add} disabled={saving}>
          {saving ? "Saving…" : "Add change"}
        </Button>
      </div>
    </div>
  );
};

export default DriverPayArrangements;
